import React, { useEffect, useState } from "react";
import { Play } from "lucide-react";
import { apiGet } from "../../../Api/Client.js";
import { isPlayable, playHref, stamp } from "./CallPlayer.jsx";
import { MISS_AT, scoreColor } from "./callMeta.js";
import LoadFailed from "./LoadFailed.jsx";

// Where this person actually lacks — not their lowest score.
//
// This used to be computed in the browser from a rep's ENTIRE call history,
// with its own threshold (below 7) that disagreed with the patterns panel's
// (at or below 5, a "miss"), no time window at all, and a bar whose length
// was the SHORTFALL — so the worst score drew the longest red bar, printed
// right beside the number it contradicted.
//
// It is now the server's own `gaps` (Backend/Products/revops/patterns.py),
// computed from the SAME window and the SAME call rows as
// everything else on this page: a gap fixed three months ago no longer reads
// as today's habit, and "avg 3.1 over 7 calls" means the same thing here as
// it does on the patterns panel, because it comes from the same query.
//
// Same default window as PatternsPanel (7 days for an SDR, 30 for an AE) —
// there is one rep-facing default, not two panels quietly disagreeing about
// what "recent" means.

const MUTED = "var(--c-500)";

function Bar({ gap }) {
  // Out of 10, like every other bar on the page. It used to be out of the
  // TARGET, so a 4.3 filled 61% of this bar and 43% of the ScoreBar beside it,
  // and "4.3 / 7" read as a score out of seven.
  const pct = Math.max(0, Math.min(100, gap.mean * 10));
  const targetPct = Math.min(100, gap.target * 10);
  const color = scoreColor(gap.mean);
  const worst = gap.worst;
  const href = worst?.at_seconds != null ? playHref(worst.call_link, worst.at_seconds) : null;

  return (
    <div style={{ padding: "9px 0", borderTop: "1px solid var(--c-200)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--c-900)" }}>{gap.label}</span>
        <span style={{ fontSize: 12.5, fontVariantNumeric: "tabular-nums", color: "var(--c-700)" }}>
          <strong style={{ color }}>{gap.mean}</strong>
          <span style={{ color: MUTED, fontWeight: 400 }}> avg · target {gap.target}</span>
        </span>
      </div>
      {/* Bar length is the SCORE, same direction as every other bar on this
          page (ScoreBar, BandGauge) — longer is better. A marker at the
          target makes the gap visible without inverting the fill. */}
      <div style={{ position: "relative", height: 6, borderRadius: 99,
                    background: "var(--c-100, rgba(120,120,120,.1))" }}>
        <div style={{ height: "100%", width: `${pct}%`, borderRadius: 99, background: color }} />
        <div style={{ position: "absolute", top: -2, bottom: -2, left: `${targetPct}%`,
                      width: 2, background: "var(--c-400)" }}
             title={`Target: ${gap.target}`} />
      </div>
      <p style={{ margin: "5px 0 0", fontSize: 11.5, color: MUTED }}>
        {gap.habit
          ? `Below target on ${gap.below_target} of ${gap.calls} calls this window — a habit, not a bad day.`
          : `Low on ${gap.below_target} of ${gap.calls} calls this window — worth watching, not yet a pattern.`}
        {gap.misses > 0 && ` ${gap.misses} scored ${MISS_AT} or below.`}
      </p>
      {worst && !worst.heard && worst.why && (
        // A voice score is backed by how the call sounded, not a line, so the
        // reason is the evidence. It used to show nothing here -- or worse,
        // a quote borrowed from a call that went fine.
        <p style={{ margin: "5px 0 0", fontSize: 12, color: "var(--c-700)" }}>
          <strong style={{ color: "var(--c-900)" }}>{worst.score}/10</strong>
          {worst.company_name ? ` with ${worst.company_name}` : ""}: {worst.why}
        </p>
      )}
      {worst?.heard && (
        <blockquote style={{ margin: "5px 0 0", padding: "5px 0 5px 10px",
                             borderLeft: "2px solid var(--c-300)", fontSize: 12,
                             fontStyle: "italic", color: "var(--c-700)", lineHeight: 1.5 }}>
          “{worst.heard}”
          {worst.quote_speaker && (
            <span style={{ fontStyle: "normal", fontSize: 11, color: MUTED, marginLeft: 6 }}>
              — said by {worst.quote_speaker}
            </span>
          )}
          {worst.at_seconds != null && href && (
            <a href={href} target="_blank" rel="noreferrer"
               style={{ fontStyle: "normal", marginLeft: 8, fontSize: 11, color: "var(--blue-600)",
                        textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 3,
                        whiteSpace: "nowrap" }}>
              {isPlayable(worst.call_link) ? <Play size={10} /> : null} {stamp(worst.at_seconds)}
            </a>
          )}
        </blockquote>
      )}
    </div>
  );
}

export default function GapBars({ employeeId, track = "junior" }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // Another person or window: clear the old rows first, so they are never read under the new name.
    setData(null);
    setError("");
    if (!employeeId) return undefined;
    let cancelled = false;
    const window = track === "senior" ? 30 : 7;
    apiGet(`/RevOps/recurring-mistakes/?employee=${employeeId}&window=${window}`)
      .then((payload) => !cancelled && setData(payload))
      // A failed request is not "no gaps": say so, rather than telling a rep there is nothing to work on.
      .catch((err) => !cancelled && setError(err?.message || "Couldn't load where you lack."));
    return () => { cancelled = true; };
  }, [employeeId, track, attempt]);

  const gaps = data?.gaps || [];

  return (
    <div>
      <div style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase",
                    letterSpacing: ".04em", color: "var(--c-600)", marginBottom: 6 }}>
        Where you lack
        {data && (
          <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, marginLeft: 6, color: MUTED }}>
            — last {data.window_days} days
          </span>
        )}
      </div>
      {error ? (
        <LoadFailed message={error} onRetry={() => setAttempt((n) => n + 1)} />
      ) : !data ? (
        <p style={{ color: MUTED, fontSize: 13, margin: 0 }}>Loading…</p>
      ) : gaps.length === 0 ? (
        <p style={{ color: MUTED, fontSize: 13, margin: 0 }}>
          Nothing below target in this window — no standing gap to work on.
        </p>
      ) : (
        <div style={{ margin: "4px -2px 0" }}>{gaps.map((g) => <Bar key={g.parameter_key} gap={g} />)}</div>
      )}
    </div>
  );
}
